// src/lib/api.js
import { islandScopedDocumentFilter, sanityFetch } from './sanity';
import { normalizeActivityDocuments } from './activityTaxonomy';

export async function fetchAllRestaurants() {
  // GROQ query pulling exactly the fields our UI needs
  const query = `*[
    _type == "restaurant" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active"
  ]{
    _id,
    island,
    name,
    "slug": slug.current,
    "area": coalesce(areaRef->title, area),
    "region": areaRef->region->title,
    "areaSlug": areaRef->slug.current,
    "regionSlug": areaRef->region->slug.current,
    serviceType,
    priceRange,
    cuisineCategory,
    cuisine,
    shortDescription,
    operationalStatus,
    placeIdentityStatus,
    humanReviewedAt,
    imageSelectionReviewedAt,
    images[]{
      asset->{
        _id,
        url
      },
      alt
    },
    placeID,
    location
  }`;

  // Fire the query
  return await sanityFetch(query);
}

export async function fetchAllLodgings() {
  const query = `*[_type == "lodging" && ${islandScopedDocumentFilter()}]{
    _id,
    island,
    name,
    "slug": slug.current,
    "area": coalesce(areaRef->title, area),
    "region": areaRef->region->title,
    "areaSlug": areaRef->slug.current,
    "regionSlug": areaRef->region->slug.current,
    priceRange,
    shortDescription,
    features,
    images[]{
      asset->{
        _id,
        url
      },
      alt
    },
    placeID,
    location
  }`;

  return await sanityFetch(query);
}

export async function fetchAllActivities() {
  const query = `*[
    _type == "activity" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active"
  ]{
    _id,
    island,
    name,
    "slug": slug.current,
    "area": coalesce(areaRef->title, area),
    "region": areaRef->region->title,
    "areaSlug": areaRef->slug.current,
    "regionSlug": areaRef->region->slug.current,
    category,
    inventoryClass,
    activityType,
    experienceTags,
    cost,
    difficulty,
    shortDescription,
    features,
    images[]{
      asset->{ _id, url },
      alt
    },
    placeID,
    location
  }`;

  return normalizeActivityDocuments(await sanityFetch(query));
}

export async function fetchAllPhotoSpots() {
  const photoSpotProjection = `{
    _id,
    name,
    shortDescription,
    "slug": slug.current,
    island,
    "region": coalesce(areaRef->region->title, regionRef->title, region),
    "regionSlug": coalesce(areaRef->region->slug.current, regionRef->slug.current),
    "area": coalesce(areaRef->title, area),
    "areaSlug": areaRef->slug.current,
    offshoreOrBackcountry,
    location,
    locationPrecision,
    placeID,
    photoCategories,
    primaryViewAzimuth,
    bestSeasonNotes,
    phonePhotoTips,
    cameraPhotoTips,
    logisticsNotes,
    _updatedAt,
    images[]{
      asset->{ _id, url },
      alt,
      attribution{
        authorName,
        authorUrl,
        sourceUrl,
        license,
        licenseUrl
      }
    },
    relatedProfile->{
      _id,
      _type,
      "name": coalesce(name, title),
      "slug": slug.current,
      "image": select(
        _type == "blogPost" => mainImage{asset->{ _id, url }, alt},
        _type == "restaurant" => images[0]{asset->{ _id, url }, alt},
        _type == "lodging" => images[0]{asset->{ _id, url }, alt},
        _type == "activity" => images[0]{asset->{ _id, url }, alt}
      )
    },
    seo{
      title,
      description,
      noindex
    }
  }`

  const query = `*[_type == "photoSpot" && defined(slug.current) && !(_id in path("drafts.**")) && ${islandScopedDocumentFilter()}] | order(name asc) ${photoSpotProjection}`;

  return await sanityFetch(query);
}

export async function fetchPhotoSpotBySlug(slug) {
  const photoSpotProjection = `{
    _id,
    name,
    shortDescription,
    "slug": slug.current,
    island,
    "region": coalesce(areaRef->region->title, regionRef->title, region),
    "regionSlug": coalesce(areaRef->region->slug.current, regionRef->slug.current),
    "area": coalesce(areaRef->title, area),
    "areaSlug": areaRef->slug.current,
    offshoreOrBackcountry,
    location,
    locationPrecision,
    placeID,
    photoCategories,
    primaryViewAzimuth,
    bestSeasonNotes,
    phonePhotoTips,
    cameraPhotoTips,
    logisticsNotes,
    _updatedAt,
    images[]{
      asset->{ _id, url },
      alt,
      attribution{
        authorName,
        authorUrl,
        sourceUrl,
        license,
        licenseUrl
      }
    },
    relatedProfile->{
      _id,
      _type,
      "name": coalesce(name, title),
      "slug": slug.current,
      "image": select(
        _type == "blogPost" => mainImage{asset->{ _id, url }, alt},
        _type == "restaurant" => images[0]{asset->{ _id, url }, alt},
        _type == "lodging" => images[0]{asset->{ _id, url }, alt},
        _type == "activity" => images[0]{asset->{ _id, url }, alt}
      )
    },
    seo{
      title,
      description,
      noindex
    }
  }`

  const query = `*[_type == "photoSpot" && defined(slug.current) && !(_id in path("drafts.**")) && ${islandScopedDocumentFilter()} && slug.current == $slug][0] ${photoSpotProjection}`
  return await sanityFetch(query, { slug })
}

export async function fetchGuidePagePreviewsByPageTypes(pageTypes = []) {
  const query = `*[_type == "guidePage"
    && ${islandScopedDocumentFilter()}
    && defined(slug.current)
    && !(_id in path("drafts.**"))
    && coalesce(seo.noindex, false) != true
    && pageType in $pageTypes
  ] | order(title asc) {
    _id,
    title,
    "slug": slug.current,
    pageType,
    hubSpoke,
    heroImage,
    seo{
      description,
      noindex
    }
  }`

  return await sanityFetch(query, { pageTypes })
}
